import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  connectSynergyWallet,
  getInjectedSynergyProvider,
  signSynergyWalletMessage,
  shortenAddress,
  type ConnectedWallet,
} from "./lib/wallet";
import {
  createHorizonWalletChallenge,
  fetchHorizonPath,
  fetchHorizonResource,
  postHorizonPath,
  requestHorizonChatStream,
  type HorizonApiResult,
  type HorizonWalletProof,
  verifyHorizonWalletChallenge,
} from "./lib/horizon-api";
import {
  captureWalletLinkCallback,
  createWalletLinkMessageAction,
  persistWalletLinkAuthorization,
  pollWalletPairingSession,
  qrToDataUrl,
  resumeWalletLinkAuthorization,
  startWalletLinkAuthorization,
  waitForWalletLinkAction,
} from "./lib/wallet-link";

type StaticRoute = "/" | "/explore" | "/models" | "/agents" | "/workflows" | "/dashboard";
type Route = StaticRoute | "/wallet/callback" | "/models/qwen3-1.7b/playground" | `/models/${string}`;
type ResourceName = "models" | "agents" | "workflows";
type HorizonSession = { token: string; expiresAt: string };
type HorizonSessionController = {
  session: HorizonSession | null;
  ensureSession: () => Promise<string>;
};

const routeItems: ReadonlyArray<{ href: StaticRoute; label: string }> = [
  { href: "/", label: "Home" },
  { href: "/explore", label: "Explore" },
  { href: "/models", label: "Models" },
  { href: "/agents", label: "Agents" },
  { href: "/workflows", label: "Workflows" },
  { href: "/dashboard", label: "Dashboard" },
];

const agentVisuals = [
  { file: "purple", label: "Purple Horizon agent" },
  { file: "blue", label: "Blue Horizon agent" },
  { file: "cyan", label: "Cyan Horizon agent" },
  { file: "lime", label: "Lime Horizon agent" },
  { file: "yellow", label: "Yellow Horizon agent" },
  { file: "orange", label: "Orange Horizon agent" },
  { file: "red", label: "Red Horizon agent" },
  { file: "gray", label: "Gray Horizon agent" },
  { file: "snrg", label: "Synergy Horizon agent" },
] as const;

function currentRoute(pathname = window.location.pathname): Route {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (routeItems.some(({ href }) => href === normalized)) return normalized as StaticRoute;
  if (normalized === "/wallet/callback") return normalized;
  if (normalized === "/models/qwen3-1.7b/playground") return normalized;
  return /^\/models\/[a-z0-9]+(?:[.-][a-z0-9]+)*$/.test(normalized) ? normalized as Route : "/";
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" className="icon icon-arrow" viewBox="0 0 24 24">
      <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg aria-hidden="true" className="icon" viewBox="0 0 24 24">
      <path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function ThemeIcon({ dark }: { dark: boolean }) {
  return dark ? (
    <svg aria-hidden="true" className="icon" viewBox="0 0 24 24"><path d="M20 14.8A8.2 8.2 0 0 1 9.2 4 8.4 8.4 0 1 0 20 14.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
  ) : (
    <svg aria-hidden="true" className="icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" /></svg>
  );
}

function ResourceState({ result, compact = false }: { result: HorizonApiResult | null; compact?: boolean }) {
  if (!result) {
    return <p className={compact ? "resource-state compact" : "resource-state"}>Checking the live Horizon service…</p>;
  }

  if (result.available) {
    return <p className={compact ? "resource-state success compact" : "resource-state success"}>Live service responded successfully.</p>;
  }

  return (
    <p className={compact ? "resource-state compact" : "resource-state"}>
      {result.message} <code>{result.status}</code>
    </p>
  );
}

function useResource(resource: ResourceName | "health", loadImmediately = true, sessionToken?: string) {
  const [result, setResult] = useState<HorizonApiResult | null>(null);
  const [loading, setLoading] = useState(loadImmediately);

  const refresh = useCallback(async () => {
    setLoading(true);
    setResult(await fetchHorizonResource(resource, sessionToken));
    setLoading(false);
  }, [resource, sessionToken]);

  useEffect(() => {
    if (loadImmediately) void refresh();
  }, [loadImmediately, refresh]);

  return { result, loading, refresh };
}

function useHorizonPath(path: string, sessionToken?: string, enabled = true) {
  const [result, setResult] = useState<HorizonApiResult | null>(null);
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    setLoading(true);
    setResult(await fetchHorizonPath(path, sessionToken));
    setLoading(false);
  }, [path, sessionToken]);

  useEffect(() => {
    if (enabled) {
      void refresh();
      return;
    }
    setResult(null);
    setLoading(false);
  }, [enabled, refresh]);

  return { result, loading, refresh };
}

function Header({
  dark,
  route,
  wallet,
  connectingWallet,
  onToggleTheme,
  onNavigate,
  onConnectWallet,
}: {
  dark: boolean;
  route: Route;
  wallet: ConnectedWallet | null;
  connectingWallet: boolean;
  onToggleTheme: () => void;
  onNavigate: (route: Route) => void;
  onConnectWallet: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = (href: Route) => {
    onNavigate(href);
    setMenuOpen(false);
  };
  const walletLabel = connectingWallet ? "Connecting…" : wallet ? shortenAddress(wallet.address) : "Connect wallet";

  useEffect(() => {
    setMenuOpen(false);
  }, [route]);

  return (
    <header className="site-header">
      <button aria-label="Go to Horizon home" className="brand-banner" onClick={() => navigate("/")} type="button">
        <img alt="Synergy Horizon" src="/brand/horizon-banner-new.png" />
      </button>
      <button aria-expanded={menuOpen} aria-label="Toggle navigation" className="menu-toggle" onClick={() => setMenuOpen((open) => !open)} type="button"><MenuIcon /></button>
      <nav aria-label="Primary navigation" className={menuOpen ? "primary-nav is-open" : "primary-nav"}>
        {routeItems.map(({ href, label }) => (
          <button aria-current={route === href || (href === "/models" && route.startsWith("/models/")) ? "page" : undefined} className={route === href || (href === "/models" && route.startsWith("/models/")) ? "active" : ""} key={href} onClick={() => navigate(href)} type="button">{label}</button>
        ))}
      </nav>
      <div className="header-actions">
        <button aria-label="Switch theme" className="icon-button" onClick={onToggleTheme} type="button"><ThemeIcon dark={dark} /></button>
        <button aria-label={wallet ? `Connected wallet ${wallet.address}` : "Connect Synergy Wallet"} className="wallet-button" onClick={onConnectWallet} type="button"><span className="wallet-desktop-label">{walletLabel}</span><span aria-hidden="true" className="wallet-mobile-label">Wallet</span></button>
      </div>
    </header>
  );
}

function Footer({ onNavigate }: { onNavigate: (route: Route) => void }) {
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <img alt="Synergy Horizon" src="/brand/horizon-banner-new.png" />
          <p>Build, deploy, and scale decentralized intelligent applications on the Synergy Network.</p>
        </div>
        <div className="footer-links" aria-label="Horizon product links">
          <div><h2>Product</h2>{routeItems.slice(1, 4).map((item) => <button key={item.href} onClick={() => onNavigate(item.href)} type="button">{item.label}</button>)}</div>
          <div><h2>Platform</h2>{routeItems.slice(4).map((item) => <button key={item.href} onClick={() => onNavigate(item.href)} type="button">{item.label}</button>)}<a href="https://api.horizon.synergy-network.io/v1/health" rel="noreferrer" target="_blank">API status</a></div>
          <div><h2>Network</h2><a href="https://synergy-network.io" rel="noreferrer" target="_blank">Synergy Network</a><a href="https://horizon.synergy-network.io" rel="noreferrer" target="_blank">Horizon</a></div>
        </div>
      </div>
      <div className="footer-bottom"><span>© {new Date().getFullYear()} Synergy Horizon. All rights reserved.</span><span>Decentralized AI, built for accountable execution.</span></div>
    </footer>
  );
}

function WalletAlert({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <aside aria-live="assertive" className="wallet-alert" role="alert"><span>{message}</span><button aria-label="Dismiss wallet message" onClick={onDismiss} type="button">×</button></aside>;
}

function HomePage({ onNavigate, onConnectWallet }: { onNavigate: (route: Route) => void; onConnectWallet: () => void }) {
  return (
    <>
      <section className="home-hero page-enter">
        <div className="hero-copy">
          <h1>The Decentralized <span>AI Hub</span></h1>
          <p>Build, deploy, and scale intelligent applications, agents, and models across the Synergy Ecosystem — all on decentralized, quantum-safe infrastructure.</p>
          <div className="hero-actions"><button className="button primary" onClick={() => onNavigate("/explore")} type="button">Explore Hub <ArrowIcon /></button><button className="button secondary" onClick={onConnectWallet} type="button">Launch App</button></div>
        </div>
        <div className="hero-visual" aria-label="Horizon visual identity">
          <div className="hero-grid" />
          <div className="hero-orbit orbit-one" /><div className="hero-orbit orbit-two" />
          <img alt="Horizon decentralized AI" className="hero-logo" src="/brand/horizon-logo-new.png" />
          <img alt="" className="hero-agent agent-purple" src="/brand/agents/purple.png" />
          <img alt="" className="hero-agent agent-cyan" src="/brand/agents/cyan.png" />
          <img alt="" className="hero-agent agent-lime" src="/brand/agents/lime.png" />
        </div>
      </section>
      <section className="feature-band page-enter delayed">
        <div><p className="eyebrow">Powering the future of decentralized intelligence</p><h2>One platform, one accountable surface.</h2></div>
        <div className="feature-list">
          <article><b>Decentralized</b><p>Open services and verifiable ownership are first-class platform concerns.</p></article>
          <article><b>Privacy-first</b><p>Connect through the Synergy Wallet browser provider or the encrypted Wallet Link mobile flow.</p></article>
          <article><b>Interoperable</b><p>Web, native clients, and services are designed around the same API contract.</p></article>
        </div>
      </section>
      <section aria-labelledby="horizon-next-title" className="launches-section page-enter delayed-more">
        <div className="launches-heading">
          <p className="eyebrow">On the Horizon</p>
          <h2 id="horizon-next-title">The next intelligence is being prepared in the open.</h2>
          <p>These releases stay clearly marked until their live service, access controls, and operational checks are complete.</p>
        </div>
        <div className="launches-grid">
          <article className="launch-card launch-card-jarvis">
            <div className="launch-card-copy">
              <span className="launch-status">Coming soon</span>
              <p className="launch-card-kicker">Synergy Network AI Agent Assistant</p>
              <h3>Jarvis</h3>
              <p>Our network AI Agent Assistant is taking shape for guided, accountable work across the Synergy ecosystem.</p>
            </div>
            <img alt="Gradient Synergy Horizon robot representing Jarvis" src="/brand/agents/snrg.png" />
          </article>
          <article className="launch-card launch-card-qwen">
            <div className="launch-card-copy">
              <span className="launch-status">Live</span>
              <p className="launch-card-kicker">First local Horizon model</p>
              <h3>Qwen3 1.7B</h3>
              <p>Verified Q8_0 inference now runs on Validator 4 through a Synergy Wallet-authenticated Horizon session.</p>
              <button className="model-detail-link" onClick={() => onNavigate("/models/qwen3-1.7b")} type="button">Open Qwen3 1.7B <ArrowIcon /></button>
            </div>
            <img alt="Blue Horizon robot representing the Qwen model rollout" src="/brand/agents/blue.png" />
          </article>
        </div>
      </section>
      <section className="ecosystem-section page-enter delayed-more">
        <div className="section-intro"><p className="eyebrow">Explore the ecosystem</p><h2>Choose the building block that fits the work.</h2><button className="text-link" onClick={() => onNavigate("/explore")} type="button">Explore everything <ArrowIcon /></button></div>
        <div className="ecosystem-grid">
          <EcosystemCard title="Models" text="Browse models registered by their authoritative Horizon service." image="blue" onClick={() => onNavigate("/models")} />
          <EcosystemCard title="Agents" text="Discover authenticated agents and their declared capabilities." image="purple" onClick={() => onNavigate("/agents")} />
          <EcosystemCard title="Workflows" text="Combine components into repeatable, accountable execution." image="cyan" onClick={() => onNavigate("/workflows")} />
        </div>
      </section>
      <section className="steps-section">
        <div className="section-intro"><p className="eyebrow">Get started in three steps</p><h2>Connect, discover, build.</h2><p>No fabricated usage figures, seeded directories, or placeholder deployments.</p></div>
        <ol className="steps-list"><li><span>01</span><h3>Connect your wallet</h3><p>Use the Synergy Wallet browser provider or Wallet Link mobile flow to establish your session.</p></li><li><span>02</span><h3>Choose a live service</h3><p>Explore the registered models, agents, and workflows that the API returns.</p></li><li><span>03</span><h3>Execute with proof</h3><p>Run only against enabled Horizon services and inspect their returned state.</p></li></ol>
      </section>
      <section className="callout-section"><div><p className="eyebrow">Horizon is live at the edge</p><h2>Ready to build your first decentralized AI project?</h2><p>Connect a supported Synergy Wallet to use the live platform services as they come online.</p></div><div className="callout-actions"><button className="button primary" onClick={onConnectWallet} type="button">Connect wallet</button><button className="button secondary" onClick={() => onNavigate("/explore")} type="button">Explore Hub <ArrowIcon /></button></div></section>
    </>
  );
}

function EcosystemCard({ title, text, image, onClick }: { title: string; text: string; image: "purple" | "cyan" | "blue"; onClick: () => void }) {
  return <button className="ecosystem-card" onClick={onClick} type="button"><img alt="" src={`/brand/agents/${image}.png`} /><span><strong>{title}</strong><small>{text}</small><i>View {title} <ArrowIcon /></i></span></button>;
}

type RegistryItem = {
  id?: string;
  slug?: string;
  display_name?: string;
  description?: string;
  publisher?: string;
  license?: string;
  lifecycle_state?: string;
  publication_status?: string;
};

function itemsFromPayload(payload: unknown): RegistryItem[] {
  if (Array.isArray(payload)) return payload.filter(isRegistryItem);
  if (!payload || typeof payload !== "object") return [];

  const collection = payload as { items?: unknown; data?: unknown };
  if (Array.isArray(collection.items)) return collection.items.filter(isRegistryItem);
  if (Array.isArray(collection.data)) return collection.data.filter(isRegistryItem);
  return [];
}

function isRegistryItem(value: unknown): value is RegistryItem {
  return Boolean(value) && typeof value === "object";
}

function SearchPage({ resource, title, description, onNavigate }: { resource: ResourceName; title: string; description: string; onNavigate: (route: Route) => void }) {
  const { result, loading, refresh } = useResource(resource);
  const [query, setQuery] = useState("");
  const items = result ? itemsFromPayload(result.payload) : [];
  const hasResults = result?.available && items.length > 0;
  return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Horizon registry</p><h1>{title}</h1><p>{description}</p></div><div className="directory-toolbar"><label className="search-field"><span>Search</span><input onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${resource}…`} value={query} /></label><button className="button secondary" disabled={loading} onClick={() => void refresh()} type="button">{loading ? "Refreshing…" : "Refresh live service"}</button></div>{hasResults ? <LiveResults items={items} onOpenModel={resource === "models" ? (slug) => onNavigate(`/models/${slug}`) : undefined} query={query} resource={resource} /> : <section className="empty-registry"><img alt="" src={`/brand/agents/${resource === "agents" ? "purple" : resource === "models" ? "blue" : "cyan"}.png`} /><div><h2>No live {resource} are available yet.</h2><ResourceState result={result} /><p>Horizon will render registered records from its authoritative API as soon as this service is enabled.</p></div></section>}{resource === "agents" && <AgentVisualGallery />}</main>;
}

function AgentVisualGallery() {
  return <section className="agent-identity-gallery"><div><p className="eyebrow">Horizon agent visual language</p><h2>Supplied identity artwork, not a live directory.</h2></div><div className="agent-identity-grid">{agentVisuals.map(({ file, label }) => <img alt={label} key={file} src={`/brand/agents/${file}.png`} />)}</div></section>;
}

function LiveResults({ items, onOpenModel, query, resource }: { items: RegistryItem[]; onOpenModel?: (slug: string) => void; query: string; resource: ResourceName }) {
  const visibleItems = items.filter((item) => JSON.stringify(item).toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="live-results" aria-live="polite">
      <p>{visibleItems.length} live {resource} returned by the Horizon API.</p>
      <div className="registry-result-grid">
        {visibleItems.map((item) => (
          <article className="registry-result-card" key={item.id ?? item.slug ?? item.display_name}>
            <div className="registry-result-heading">
              <div>
                <p className="eyebrow">{item.slug ?? resource.slice(0, -1)}</p>
                <h2>{item.display_name ?? item.slug ?? "Unnamed registered service"}</h2>
              </div>
              {item.publication_status && <span className="status-chip">{item.publication_status.replaceAll("_", " ")}</span>}
            </div>
            {item.description && <p className="registry-result-description">{item.description}</p>}
            <dl className="registry-result-meta">
              {item.publisher && <div><dt>Publisher</dt><dd>{item.publisher}</dd></div>}
              {item.license && <div><dt>License</dt><dd>{item.license}</dd></div>}
              {item.lifecycle_state && <div><dt>State</dt><dd>{item.lifecycle_state.replaceAll("_", " ")}</dd></div>}
            </dl>
            {onOpenModel && item.slug && <button className="model-detail-link" onClick={() => onOpenModel(item.slug!)} type="button">View model details <ArrowIcon /></button>}
          </article>
        ))}
      </div>
    </section>
  );
}

type DetailRecord = Record<string, unknown>;

function record(value: unknown): DetailRecord | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as DetailRecord : null;
}

function recordFromPayload(payload: unknown, key: string): DetailRecord | null {
  if (!payload || typeof payload !== "object") return null;
  const value = (payload as Record<string, unknown>)[key];
  return value && typeof value === "object" && !Array.isArray(value) ? value as DetailRecord : null;
}

function recordsFromPayload(payload: unknown, key: string): DetailRecord[] {
  if (!payload || typeof payload !== "object") return [];
  const value = (payload as Record<string, unknown>)[key];
  return Array.isArray(value) ? value.filter((item): item is DetailRecord => Boolean(item) && typeof item === "object" && !Array.isArray(item)) : [];
}

function displayValue(value: unknown): string | null {
  return typeof value === "string" || typeof value === "number" ? String(value) : null;
}

function DetailFact({ label, value }: { label: string; value: unknown }) {
  const text = displayValue(value);
  return text ? <div><dt>{label}</dt><dd>{text}</dd></div> : null;
}

type ChatMessage = { id: string; role: "user" | "assistant"; content: string };

function QwenChatInterface({ wallet, onConnectWallet, sessionControl }: { wallet: ConnectedWallet | null; onConnectWallet: () => void; sessionControl: HorizonSessionController }) {
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [requestId, setRequestId] = useState<string | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const hasActiveConversationSession = Boolean(sessionControl.session && Date.parse(sessionControl.session.expiresAt) > Date.now() + 10_000);

  useEffect(() => {
    setMessages([]);
    setError("");
    setRequestId(null);
    abortRef.current?.abort();
  }, [wallet?.address]);

  useEffect(() => {
    const transcript = transcriptRef.current;
    if (transcript) transcript.scrollTo({ top: transcript.scrollHeight, behavior: "smooth" });
  }, [messages, running]);

  useEffect(() => {
    if (!running || !startedAtRef.current) return undefined;
    const timer = window.setInterval(() => setElapsedMs(Date.now() - startedAtRef.current!), 100);
    return () => window.clearInterval(timer);
  }, [running]);

  const send = async (input: string, history: ChatMessage[]) => {
    if (!wallet) {
      onConnectWallet();
      return;
    }
    const content = input.trim();
    if (!content) {
      setError("Enter a message for Qwen3 1.7B.");
      return;
    }
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", content };
    const assistantMessage: ChatMessage = { id: crypto.randomUUID(), role: "assistant", content: "" };
    const conversation = [...history, userMessage];
    const controller = new AbortController();
    abortRef.current = controller;
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setRequestId(null);
    setError("");
    setRunning(true);
    setMessages([...conversation, assistantMessage]);
    setDraft("");
    let receivedContent = false;
    try {
      const token = await sessionControl.ensureSession();
      const result = await requestHorizonChatStream(
        token,
        conversation.slice(-16).map(({ role, content: messageContent }) => ({ role, content: messageContent })),
        (delta) => {
          receivedContent = true;
          setMessages((current) => current.map((message) => message.id === assistantMessage.id ? { ...message, content: `${message.content}${delta}` } : message));
        },
        controller.signal,
      );
      if (!receivedContent) throw new Error("Horizon inference completed without an assistant response.");
      setRequestId(result.requestId);
    } catch (failure) {
      if (controller.signal.aborted) {
        setMessages((current) => current.filter((message) => message.id !== assistantMessage.id || message.content));
      } else {
        setMessages((current) => current.filter((message) => message.id !== assistantMessage.id || message.content));
        setError(failure instanceof Error ? failure.message : "Qwen3 1.7B could not complete this request.");
      }
    } finally {
      setElapsedMs(Date.now() - (startedAtRef.current ?? Date.now()));
      startedAtRef.current = null;
      abortRef.current = null;
      setRunning(false);
    }
  };

  const retryLast = () => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].role === "user") {
        void send(messages[index].content, messages.slice(0, index));
        return;
      }
    }
  };

  const copyLastAssistant = async () => {
    const last = [...messages].reverse().find((message) => message.role === "assistant" && message.content);
    if (!last) return;
    try {
      await navigator.clipboard.writeText(last.content);
    } catch {
      setError("Your browser did not permit copying this response.");
    }
  };

  return <section className="model-detail-panel inference-panel qwen-playground-panel">
    <div className="playground-heading"><div><p className="eyebrow">Live Qwen3 1.7B playground</p><h2>Chat with the verified deployment</h2><p>The first action in this active Horizon session requests a Synergy Wallet signature. Streaming responses, request IDs, and elapsed time are returned from the real protected inference path. No message charge is applied.</p></div><img alt="Blue Horizon robot representing Qwen3 1.7B" src="/brand/agents/blue.png" /></div>
    <div aria-live="polite" aria-relevant="additions text" className="chat-transcript" ref={transcriptRef}>
      {messages.length === 0 && !running && <div className="chat-empty"><img alt="Qwen3 1.7B Horizon agent" src="/brand/agents/blue.png" /><p>Start a real conversation with the verified Qwen3 1.7B deployment on Validator 4.</p></div>}
      {messages.map((message) => <article className={`chat-message ${message.role}`} key={message.id}><img alt="" src={`/brand/agents/${message.role === "assistant" ? "blue" : "purple"}.png`} /><div><p>{message.role === "assistant" ? "Qwen3 1.7B" : "You"}</p><span>{message.content}</span></div></article>)}
      {running && <article aria-label="Qwen3 1.7B is typing" className="chat-message assistant typing-indicator"><img alt="" src="/brand/agents/blue.png" /><div><p>Qwen3 1.7B</p><span><i /><i /><i /></span></div></article>}
    </div>
    <div className="playground-status" aria-live="polite"><span>{running ? `Streaming • ${(elapsedMs / 1000).toFixed(1)}s` : elapsedMs ? `Last response • ${(elapsedMs / 1000).toFixed(1)}s` : "Ready"}</span>{requestId && <code>{requestId}</code>}</div>
    <form className="chat-composer" onSubmit={(event) => { event.preventDefault(); void send(draft, messages); }}>
      <label className="inference-input"><span>Message</span><textarea disabled={running} maxLength={4096} onChange={(event) => setDraft(event.target.value)} placeholder="Message Qwen3 1.7B…" value={draft} /></label>
      <div className="inference-actions"><button className="button primary" disabled={running || (!wallet ? false : !draft.trim())} type="submit">{running ? "Qwen is typing…" : wallet ? hasActiveConversationSession ? "Send message" : "Authorize & send" : "Connect wallet to chat"}</button>{running && <button className="button secondary" onClick={() => abortRef.current?.abort()} type="button">Stop</button>}<button className="button secondary" disabled={running || messages.length === 0} onClick={() => { setMessages([]); setRequestId(null); setElapsedMs(0); setError(""); }} type="button">Clear</button><button className="button secondary" disabled={running || !messages.some((message) => message.role === "assistant" && message.content)} onClick={() => void copyLastAssistant()} type="button">Copy</button><button className="button secondary" disabled={running || !messages.some((message) => message.role === "user")} onClick={retryLast} type="button">Retry</button></div>
      <p className="inference-session-note">{wallet ? hasActiveConversationSession ? "Conversation authorized. Later messages reuse this in-memory session." : `Connected: ${shortenAddress(wallet.address)}. A signature is needed before the first protected action.` : "A connected Synergy Wallet is required."}</p>
    </form>
    {error && <p aria-live="assertive" className="inference-error">{error}</p>}
  </section>;
}

function QwenPlaygroundPage({ onNavigate, wallet, onConnectWallet, sessionControl }: { onNavigate: (route: Route) => void; wallet: ConnectedWallet | null; onConnectWallet: () => void; sessionControl: HorizonSessionController }) {
  return <main className="directory-page model-detail-page page-enter"><button className="text-link" onClick={() => onNavigate("/models/qwen3-1.7b")} type="button">Back to Qwen3 1.7B <ArrowIcon /></button><QwenChatInterface wallet={wallet} onConnectWallet={onConnectWallet} sessionControl={sessionControl} /></main>;
}

function ModelDetailPage({ modelId, onNavigate }: { modelId: string; onNavigate: (route: Route) => void }) {
  const encodedId = encodeURIComponent(modelId);
  const model = useHorizonPath(`/models/${encodedId}`);
  const versions = useHorizonPath(`/models/${encodedId}/versions`);
  const deployments = useHorizonPath(`/models/${encodedId}/deployments`);
  const record = model.result ? recordFromPayload(model.result.payload, "model") : null;
  const version = versions.result ? recordsFromPayload(versions.result.payload, "versions")[0] : null;
  const deployment = deployments.result ? recordsFromPayload(deployments.result.payload, "deployments")[0] : null;

  if (!model.result || model.loading) {
    return <main className="directory-page page-enter"><section className="empty-registry"><img alt="" src="/brand/agents/blue.png" /><div><h1>Loading model details…</h1><ResourceState result={model.result} /></div></section></main>;
  }

  if (!model.result.available || !record) {
    return <main className="directory-page page-enter"><button className="text-link" onClick={() => onNavigate("/models")} type="button">Back to Models <ArrowIcon /></button><section className="empty-registry"><img alt="" src="/brand/agents/blue.png" /><div><h1>Model details are unavailable.</h1><ResourceState result={model.result} /><p>This route displays records only when the Horizon registry returns an authorized model.</p></div></section></main>;
  }

  return (
    <main className="directory-page model-detail-page page-enter">
      <button className="text-link" onClick={() => onNavigate("/models")} type="button">Back to Models <ArrowIcon /></button>
      <div className="model-detail-heading">
        <div><p className="eyebrow">{displayValue(record.slug) ?? modelId}</p><h1>{displayValue(record.display_name) ?? modelId}</h1><p>{displayValue(record.description) ?? ""}</p></div>
        {displayValue(record.publication_status) && <span className="status-chip">{displayValue(record.publication_status)!.replaceAll("_", " ")}</span>}
      </div>
      <section className="model-detail-panel">
        <h2>Model identity</h2>
        <dl className="model-detail-facts">
          <DetailFact label="Publisher" value={record.publisher} />
          <DetailFact label="License" value={record.license} />
          <DetailFact label="Source provider" value={record.source_provider} />
          <DetailFact label="Source repository" value={record.source_repository} />
          <DetailFact label="Source revision" value={record.source_revision} />
          <DetailFact label="Lifecycle" value={record.lifecycle_state} />
        </dl>
      </section>
      {version && <section className="model-detail-panel"><h2>Verified artifact and runtime</h2><dl className="model-detail-facts"><DetailFact label="Version" value={version.version} /><DetailFact label="Runtime" value={version.runtime} /><DetailFact label="Runtime version" value={version.runtime_version} /><DetailFact label="Format" value={version.artifact_format} /><DetailFact label="Quantization" value={version.quantization} /><DetailFact label="Configured context" value={version.configured_context_tokens} /><DetailFact label="Maximum context" value={version.maximum_context_tokens} /><DetailFact label="Artifact verification" value={version.validation_status} /></dl></section>}
      {deployment && <section className="model-detail-panel"><h2>Deployment health</h2><dl className="model-detail-facts"><DetailFact label="Status" value={deployment.status} /><DetailFact label="Runtime" value={deployment.runtime} /><DetailFact label="Concurrency" value={deployment.maximum_concurrency} /><DetailFact label="Configured context" value={deployment.configured_context_tokens} /><DetailFact label="Last health check" value={deployment.last_health_check_at} /></dl></section>}
      {record.slug === "qwen3-1.7b" && <section className="model-detail-panel qwen-playground-launch"><div><p className="eyebrow">Interactive model playground</p><h2>Use Qwen3 1.7B in a live streaming conversation.</h2><p>Open the dedicated interface for real typing, stop, retry, copy, elapsed-time, and request-ID controls.</p></div><button className="button primary" onClick={() => onNavigate("/models/qwen3-1.7b/playground")} type="button">Open playground <ArrowIcon /></button></section>}
      <section className="model-detail-service-state"><ResourceState compact result={versions.result} /><ResourceState compact result={deployments.result} /></section>
    </main>
  );
}

function ProtectedSessionPrompt({ wallet, onConnectWallet, onAuthorize, busy }: { wallet: ConnectedWallet | null; onConnectWallet: () => void; onAuthorize: () => void; busy: boolean }) {
  return <section className="protected-session-prompt"><img alt="Purple Horizon agent" src="/brand/agents/purple.png" /><div><p className="eyebrow">Internal beta capability</p><h2>{wallet ? "Authorize this Horizon session" : "Connect Synergy Wallet to continue"}</h2><p>{wallet ? "The first protected action uses an origin-bound FN-DSA signature. Horizon retains only a short in-memory session in this browser tab." : "A Synergy Wallet connection is required before Horizon can request a protected session."}</p></div><button className="button primary" disabled={busy} onClick={wallet ? onAuthorize : onConnectWallet} type="button">{busy ? "Authorizing…" : wallet ? "Authorize session" : "Connect wallet"}</button></section>;
}

function AgentPage({ wallet, onConnectWallet, sessionControl }: { wallet: ConnectedWallet | null; onConnectWallet: () => void; sessionControl: HorizonSessionController }) {
  const sessionToken = sessionControl.session?.token;
  const agents = useHorizonPath("/agents", sessionToken, Boolean(sessionToken));
  const agent = useHorizonPath("/agents/qwen-test-agent", sessionToken, Boolean(sessionToken));
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [runResult, setRunResult] = useState<DetailRecord | null>(null);
  const items = agents.result ? itemsFromPayload(agents.result.payload) : [];
  const definition = agent.result ? recordFromPayload(agent.result.payload, "agent") : null;

  const authorize = async () => {
    setError("");
    try {
      await sessionControl.ensureSession();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Horizon session authorization failed.");
    }
  };

  const runAgent = async () => {
    const prompt = input.trim();
    if (!prompt) {
      setError("Enter an instruction for Qwen Test Agent.");
      return;
    }
    setRunning(true);
    setError("");
    try {
      const token = await sessionControl.ensureSession();
      const payload = record(await postHorizonPath("/agents/qwen-test-agent/runs", { input: prompt }, token));
      const run = recordFromPayload(payload, "run");
      const response = recordFromPayload(payload, "response");
      if (!run || !response || typeof response.content !== "string" || !response.content.trim()) {
        throw new Error("Horizon did not return a completed Qwen Test Agent result.");
      }
      setRunResult(payload);
      setInput("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Qwen Test Agent could not complete this run.");
    } finally {
      setRunning(false);
    }
  };

  if (!sessionToken) return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Horizon Agents</p><h1>Run declared agents with accountable execution.</h1><p>Internal-beta agents stay hidden until a real Synergy Wallet session authorizes access.</p></div><ProtectedSessionPrompt busy={false} onAuthorize={() => void authorize()} onConnectWallet={onConnectWallet} wallet={wallet} /><AgentVisualGallery />{error && <p className="inference-error">{error}</p>}</main>;

  const response = runResult ? recordFromPayload(runResult, "response") : null;
  const run = runResult ? recordFromPayload(runResult, "run") : null;
  return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Horizon Agents</p><h1>Qwen Test Agent</h1><p>Its declared configuration permits Qwen inference only—no tools, network access, filesystem, wallet, or long-term memory.</p></div>{definition ? <section className="model-detail-panel"><dl className="model-detail-facts"><DetailFact label="Agent version" value={definition.agent_version_id} /><DetailFact label="Model version" value={definition.model_version_id} /><DetailFact label="Visibility" value={definition.visibility} /><DetailFact label="Publication" value={definition.publication_status} /></dl></section> : <ResourceState result={agent.result} />}<section className="model-detail-panel agent-run-panel"><div><p className="eyebrow">Execute real agent run</p><h2>Send a bounded instruction</h2></div><label className="inference-input"><span>Instruction</span><textarea disabled={running} maxLength={4096} onChange={(event) => setInput(event.target.value)} placeholder="Ask Qwen Test Agent to perform a concise validation task…" value={input} /></label><div className="inference-actions"><button className="button primary" disabled={running || !input.trim()} onClick={() => void runAgent()} type="button">{running ? "Agent is running…" : "Run Qwen Test Agent"}</button><button className="button secondary" disabled={agents.loading} onClick={() => void agents.refresh()} type="button">Refresh registry</button></div>{run && <dl className="model-detail-facts"><DetailFact label="Run status" value={run.status} /><DetailFact label="Duration (ms)" value={run.duration_ms} /><DetailFact label="Input tokens" value={run.input_token_count} /><DetailFact label="Output tokens" value={run.output_token_count} /></dl>}{response && <article className="agent-response"><p className="eyebrow">Live agent response</p><p>{displayValue(response.content)}</p></article>}</section>{items.length > 0 && <LiveResults items={items} query="" resource="agents" />}{error && <p className="inference-error">{error}</p>}<AgentVisualGallery /></main>;
}

function WorkflowPage({ wallet, onConnectWallet, sessionControl }: { wallet: ConnectedWallet | null; onConnectWallet: () => void; sessionControl: HorizonSessionController }) {
  const sessionToken = sessionControl.session?.token;
  const workflows = useHorizonPath("/workflows", sessionToken, Boolean(sessionToken));
  const [runId, setRunId] = useState("");
  const runData = useHorizonPath(runId ? `/workflow-runs/${encodeURIComponent(runId)}` : "/workflows/qwen-model-smoke-test", sessionToken, Boolean(sessionToken));
  const [starting, setStarting] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [error, setError] = useState("");
  const run = runData.result ? recordFromPayload(runData.result.payload, "run") : null;
  const steps = runData.result ? recordsFromPayload(runData.result.payload, "steps") : [];
  const definition = runData.result ? recordFromPayload(runData.result.payload, "workflow") : null;

  useEffect(() => {
    const status = displayValue(run?.status);
    if (!runId || !status || !["queued", "running", "awaiting_approval"].includes(status)) return undefined;
    const timer = window.setInterval(() => void runData.refresh(), 2000);
    return () => window.clearInterval(timer);
  }, [run?.status, runData, runId]);

  const authorize = async () => {
    setError("");
    try {
      await sessionControl.ensureSession();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Horizon session authorization failed.");
    }
  };

  const start = async () => {
    setStarting(true);
    setError("");
    try {
      const token = await sessionControl.ensureSession();
      const payload = record(await postHorizonPath("/workflows/qwen-model-smoke-test/runs", {}, token));
      const created = record(payload?.workflow_run);
      const id = typeof created?.id === "string" ? created.id : "";
      if (!id) throw new Error("Horizon did not return a durable workflow run.");
      setRunId(id);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The Qwen smoke workflow could not be started.");
    } finally {
      setStarting(false);
    }
  };

  const submitDecision = async (action: "approve" | "reject") => {
    if (!runId) return;
    setDecisionBusy(true);
    setError("");
    try {
      const token = await sessionControl.ensureSession();
      await postHorizonPath(`/workflow-runs/${encodeURIComponent(runId)}/${action}`, {}, token);
      await runData.refresh();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The workflow decision could not be submitted.");
    } finally {
      setDecisionBusy(false);
    }
  };

  if (!sessionToken) return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Horizon Workflows</p><h1>Run a durable Qwen model smoke test.</h1><p>The workflow runs real health, conversation, structured-output, and non-thinking checks before it can await a human decision.</p></div><ProtectedSessionPrompt busy={false} onAuthorize={() => void authorize()} onConnectWallet={onConnectWallet} wallet={wallet} />{error && <p className="inference-error">{error}</p>}</main>;

  return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Horizon Workflows</p><h1>Qwen model smoke test</h1><p>Each run writes its report and metrics to private Horizon storage, then requires an explicit approve or reject decision.</p></div>{definition ? <section className="model-detail-panel"><dl className="model-detail-facts"><DetailFact label="Workflow version" value={definition.workflow_version_id} /><DetailFact label="Visibility" value={definition.visibility} /><DetailFact label="Publication" value={definition.publication_status} /></dl></section> : <ResourceState result={runData.result} />}<section className="model-detail-panel workflow-run-panel"><div className="workflow-run-heading"><div><p className="eyebrow">Durable execution</p><h2>{run ? `Run ${displayValue(run.status)?.replaceAll("_", " ")}` : "Start a verification run"}</h2><p>{run ? `Workflow ${displayValue(run.id)} is backed by an authoritative durable instance.` : "This runs only against the available Qwen deployment and never fabricates a report."}</p></div><button className="button primary" disabled={starting || Boolean(runId && !["approved", "rejected", "failed", "cancelled"].includes(displayValue(run?.status) ?? ""))} onClick={() => void start()} type="button">{starting ? "Starting…" : runId ? "Start another run" : "Start smoke test"}</button></div>{run && <><dl className="model-detail-facts"><DetailFact label="Status" value={run.status} /><DetailFact label="Report reference" value={run.report_storage_reference} /><DetailFact label="Metrics reference" value={run.metrics_storage_reference} /><DetailFact label="Error code" value={run.error_code} /></dl><div className="workflow-step-list">{steps.map((step) => <article key={displayValue(step.id) ?? displayValue(step.step_key)}><span className={`status-chip workflow-${displayValue(step.status) ?? "unknown"}`}>{displayValue(step.status)?.replaceAll("_", " ")}</span><div><strong>{displayValue(step.step_key)?.replaceAll("_", " ")}</strong><small>{displayValue(step.error_code) || "Recorded by the durable workflow."}</small></div></article>)}</div>{displayValue(run.status) === "awaiting_approval" && <div className="workflow-decision"><p>The private report is complete. Choose the recorded manual decision.</p><button className="button primary" disabled={decisionBusy} onClick={() => void submitDecision("approve")} type="button">Approve</button><button className="button secondary" disabled={decisionBusy} onClick={() => void submitDecision("reject")} type="button">Reject</button></div>}</>}</section>{workflows.result && itemsFromPayload(workflows.result.payload).length > 0 && <LiveResults items={itemsFromPayload(workflows.result.payload)} query="" resource="workflows" />}{error && <p className="inference-error">{error}</p>}</main>;
}

function ExplorePage({ onNavigate }: { onNavigate: (route: Route) => void }) {
  const [search, setSearch] = useState("");
  const services = [
    { title: "Models", route: "/models" as const, image: "blue" as const, text: "Source-registered model capabilities." },
    { title: "Agents", route: "/agents" as const, image: "purple" as const, text: "Authenticated agent identities and capabilities." },
    { title: "Workflows", route: "/workflows" as const, image: "cyan" as const, text: "Composable service execution graphs." },
  ].filter(({ title, text }) => `${title} ${text}`.toLowerCase().includes(search.toLowerCase()));
  return <main className="directory-page page-enter"><div className="page-heading"><p className="eyebrow">Explore the Hub</p><h1>Discover live Horizon services.</h1><p>Models, agents, and workflows become visible only when their authoritative registry responds.</p></div><div className="directory-toolbar"><label className="search-field"><span>Search</span><input onChange={(event) => setSearch(event.target.value)} placeholder="Search the hub…" value={search} /></label></div><div className="explore-grid">{services.map((service) => <button className="explore-service" key={service.title} onClick={() => onNavigate(service.route)} type="button"><img alt="" src={`/brand/agents/${service.image}.png`} /><span><strong>{service.title}</strong><small>{service.text}</small><i>Explore <ArrowIcon /></i></span></button>)}</div></main>;
}

function DashboardPage({ wallet, onConnectWallet, sessionControl }: { wallet: ConnectedWallet | null; onConnectWallet: () => void; sessionControl: HorizonSessionController }) {
  const sessionToken = sessionControl.session?.token;
  const health = useResource("health");
  const models = useResource("models");
  const agents = useResource("agents", true, sessionToken);
  const workflows = useResource("workflows", true, sessionToken);
  const operationalData = useHorizonPath("/dashboard");
  const personalInference = useHorizonPath("/inference/runs", sessionToken, Boolean(sessionToken));
  const summary = operationalData.result ? recordFromPayload(operationalData.result.payload, "summary") : null;
  const services = useMemo(() => [
    ["API edge", health], ["Model registry", models], ["Agent registry", agents], ["Workflow registry", workflows],
  ] as const, [agents, health, models, workflows]);
  const metrics: ReadonlyArray<[string, unknown]> = summary ? [
    ["Registered models", summary.registered_models],
    ["Available deployments", summary.available_deployments],
    ["Active inference runs", summary.active_inference_runs],
    ["Successful inference runs", summary.successful_inference_runs],
    ["Failed inference runs", summary.failed_inference_runs],
    ["Active agent runs", summary.active_agent_runs],
    ["Active workflow runs", summary.active_workflow_runs],
  ] : [];
  const lastHealth = summary?.last_model_health_check;
  const healthStatus = lastHealth && typeof lastHealth === "object" ? recordFromPayload({ health: lastHealth }, "health") : null;
  const runs = personalInference.result ? recordsFromPayload(personalInference.result.payload, "items") : [];
  const sessionActivity = !sessionToken
    ? <section className="dashboard-note"><h2>Authorize a session to inspect your own execution records.</h2><p>Horizon does not show synthetic activity. The dashboard will display only run metadata returned for your signed wallet session.</p></section>
    : <section className="model-detail-panel session-run-panel"><div className="operational-panel-heading"><div><p className="eyebrow">Your authoritative run history</p><h2>Inference records</h2></div><button className="button secondary" disabled={personalInference.loading} onClick={() => void personalInference.refresh()} type="button">{personalInference.loading ? "Refreshing…" : "Refresh runs"}</button></div>{runs.length ? <div className="session-run-list">{runs.map((run) => <article key={displayValue(run.id)}><span className={`status-chip workflow-${displayValue(run.status) ?? "unknown"}`}>{displayValue(run.status)}</span><div><strong>{displayValue(run.request_id)}</strong><small>{displayValue(run.completed_at) || displayValue(run.started_at) || "Timestamp not reported"}</small></div><dl><DetailFact label="Input tokens" value={run.input_token_count} /><DetailFact label="Output tokens" value={run.output_token_count} /><DetailFact label="Duration (ms)" value={run.duration_ms} /><DetailFact label="Error" value={run.error_code} /></dl></article>)}</div> : <ResourceState result={personalInference.result} />}</section>;
  return <main className="dashboard-page page-enter"><div className="page-heading"><p className="eyebrow">Dashboard</p><h1>Your Horizon session.</h1><p>Service status is fetched from the active Horizon API; account data appears only after a verified wallet session and service response.</p></div><section className="wallet-panel"><div><p className="eyebrow">Synergy Wallet</p><h2>{wallet ? shortenAddress(wallet.address) : "No wallet connected"}</h2><p>{wallet ? `Chain ${wallet.chainId}` : "Connect through the supported Synergy Wallet browser provider."}</p></div><button className="button primary" onClick={onConnectWallet} type="button">{wallet ? "Reconnect wallet" : "Connect wallet"}</button></section><section className="service-grid">{services.map(([name, service]) => <article className="service-card" key={name}><div><p>{name}</p><h2>{service.result?.available ? "Online" : service.loading ? "Checking" : "Unavailable"}</h2></div><button aria-label={`Refresh ${name}`} className="refresh-button" disabled={service.loading} onClick={() => void service.refresh()} type="button">↻</button><ResourceState compact result={service.result} /></article>)}</section><section className="operational-panel"><div className="operational-panel-heading"><div><p className="eyebrow">Authoritative operations</p><h2>Current platform activity</h2></div><button className="button secondary" disabled={operationalData.loading} onClick={() => void operationalData.refresh()} type="button">{operationalData.loading ? "Refreshing…" : "Refresh data"}</button></div>{summary ? <><div className="operational-grid">{metrics.map(([label, value]) => <article key={label}><p>{label}</p><strong>{displayValue(value) ?? "—"}</strong></article>)}</div><p className="operational-health">{healthStatus ? `Last model health check: ${displayValue(healthStatus.status) ?? "unknown"} at ${displayValue(healthStatus.checked_at) ?? "an unreported time"}.` : "No model health check has been recorded yet."}</p></> : <ResourceState result={operationalData.result} />}</section>{sessionActivity}<section className="dashboard-note"><h2>Activity appears only when Horizon has real session-backed events to report.</h2><p>The dashboard intentionally does not seed activity, deployment, balance, inference, agent, or workflow metrics.</p></section></main>;
}

function WalletConnectionDialog({ busy, onBrowser, onClose, onMobile }: { busy: boolean; onBrowser: () => void; onClose: () => void; onMobile: () => void }) {
  return <div aria-modal="true" className="wallet-link-dialog-backdrop" role="dialog"><section className="wallet-link-dialog"><button aria-label="Close wallet connection choices" className="wallet-link-close" disabled={busy} onClick={onClose} type="button">×</button><img alt="Synergy Horizon" src="/brand/horizon-logo-new.png" /><p className="eyebrow">Connect Synergy Wallet</p><h2>Choose your secure connection.</h2><p>Browser extension connections are immediate. Mobile connections use the registered Wallet Link v2 flow, then show a QR code for your Synergy Wallet app.</p><div className="wallet-link-options"><button className="button primary" disabled={busy} onClick={onBrowser} type="button">Use browser wallet</button><button className="button secondary" disabled={busy} onClick={onMobile} type="button">{busy ? "Preparing secure link…" : "Connect mobile app by QR"}</button></div><small>Horizon requests no transaction approval and no per-message payment.</small></section></div>;
}

function WalletLinkCallbackPage({ onConnected, onError }: { onConnected: (wallet: ConnectedWallet) => void; onError: (message: string) => void }) {
  const [status, setStatus] = useState("Verifying Wallet Web Hub authorization…");
  const [qrImage, setQrImage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const captured = captureWalletLinkCallback();
    if (!captured.isCallback || captured.error || !captured.callback) {
      setError(captured.error || "Wallet Link callback was not valid.");
      return;
    }
    let cancelled = false;
    const complete = async () => {
      try {
        const linked = await resumeWalletLinkAuthorization(captured.callback);
        if (cancelled) return;
        setQrImage(await qrToDataUrl(linked.qrPayload));
        setStatus("Scan this Horizon QR code in Synergy Wallet to approve the connection.");
        while (!cancelled) {
          const result = await pollWalletPairingSession(linked);
          if (result.connected && result.session) {
            if (typeof result.address !== "string" || typeof result.chainId !== "number") throw new Error("Synergy Wallet Link approval did not include a wallet account.");
            onConnected({ address: result.address, chainId: result.chainId, source: "wallet-link", walletLinkSession: result.session });
            return;
          }
          if (result.status !== "pending") throw new Error(result.error || "The Synergy Wallet connection did not complete.");
          await new Promise((resolve) => window.setTimeout(resolve, 1_800));
        }
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Synergy Wallet Link could not finish the connection.");
      }
    };
    void complete();
    return () => { cancelled = true; };
  }, [onConnected]);

  return <main className="wallet-link-callback page-enter"><section><img alt="Horizon mobile-wallet connection" src="/brand/agents/snrg.png" /><p className="eyebrow">Synergy Wallet Link</p><h1>Connect your mobile wallet.</h1>{error ? <><p className="inference-error">{error}</p><button className="button secondary" onClick={() => onError(error)} type="button">Return to Horizon</button></> : <><p>{status}</p>{qrImage ? <img className="wallet-link-qr" alt="QR code to connect Synergy Wallet to Horizon" src={qrImage} /> : <div aria-label="Preparing wallet connection QR code" className="wallet-link-spinner" />}</>}<small>The QR encodes an expiring, origin-bound Wallet Link request. It cannot authorize a transaction.</small></section></main>;
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => currentRoute());
  const [dark, setDark] = useState(true);
  const [wallet, setWallet] = useState<ConnectedWallet | null>(null);
  const [horizonSession, setHorizonSession] = useState<HorizonSession | null>(null);
  const [walletError, setWalletError] = useState("");
  const [connectingWallet, setConnectingWallet] = useState(false);
  const [walletChoicesOpen, setWalletChoicesOpen] = useState(false);
  const authorizationPromiseRef = useRef<Promise<string> | null>(null);

  const navigate = useCallback((nextRoute: Route) => {
    if (window.location.pathname !== nextRoute) window.history.pushState({}, "", nextRoute);
    setRoute(nextRoute);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const provider = getInjectedSynergyProvider();
    if (!provider?.on || !provider.removeListener) return;
    const clearWallet = () => {
      setWallet(null);
      setHorizonSession(null);
    };
    provider.on("accountsChanged", clearWallet);
    provider.on("chainChanged", clearWallet);
    return () => { provider.removeListener?.("accountsChanged", clearWallet); provider.removeListener?.("chainChanged", clearWallet); };
  }, []);

  const connectWallet = useCallback(async () => {
    setWalletError("");
    setConnectingWallet(true);
    try {
      setWallet(await connectSynergyWallet());
      setHorizonSession(null);
      setWalletChoicesOpen(false);
    } catch (error) {
      setWalletError(error instanceof Error ? error.message : "Synergy Wallet could not complete the connection.");
    } finally {
      setConnectingWallet(false);
    }
  }, []);

  const connectMobileWallet = useCallback(async () => {
    setWalletError("");
    setConnectingWallet(true);
    try {
      const flow = await startWalletLinkAuthorization();
      await persistWalletLinkAuthorization(flow);
      window.location.assign(flow.hubAuthorizeUrl);
    } catch (error) {
      setWalletError(error instanceof Error ? error.message : "Synergy Wallet Link could not start the mobile connection.");
      setConnectingWallet(false);
    }
  }, []);

  const connectedMobileWallet = useCallback((nextWallet: ConnectedWallet) => {
    setWallet(nextWallet);
    setHorizonSession(null);
    setConnectingWallet(false);
    setWalletChoicesOpen(false);
    navigate("/dashboard");
  }, [navigate]);

  const ensureHorizonSession = useCallback(async () => {
    if (!wallet) throw new Error("Connect Synergy Wallet before authorizing Horizon.");
    if (horizonSession && Date.parse(horizonSession.expiresAt) > Date.now() + 10_000) return horizonSession.token;
    if (authorizationPromiseRef.current) return authorizationPromiseRef.current;
    const authorization = (async () => {
      const challenge = await createHorizonWalletChallenge(wallet.address);
      let signed: HorizonWalletProof;
      if (wallet.source === "wallet-link") {
        const action = await createWalletLinkMessageAction({ session: wallet.walletLinkSession, payload: challenge, account: wallet.address, fetchImpl: fetch });
        const completed = await waitForWalletLinkAction(wallet.walletLinkSession, action.requestId);
        const result = completed?.result;
        if (!result || result.signatureAlgorithm !== "FN-DSA-1024" || result.method !== "synergy_signMessage" || result.origin !== window.location.origin || result.account?.toLowerCase() !== wallet.address.toLowerCase() || result.payload?.challenge_id !== challenge.challenge_id) {
          throw new Error("Synergy Wallet Link returned an invalid Horizon message signature.");
        }
        signed = result as HorizonWalletProof;
      } else {
        signed = await signSynergyWalletMessage(wallet, challenge) as HorizonWalletProof;
      }
      const verified = await verifyHorizonWalletChallenge(challenge, signed);
      setHorizonSession({ token: verified.sessionToken, expiresAt: verified.expiresAt });
      return verified.sessionToken;
    })();
    authorizationPromiseRef.current = authorization;
    try {
      return await authorization;
    } finally {
      authorizationPromiseRef.current = null;
    }
  }, [horizonSession, wallet]);

  const sessionControl = useMemo<HorizonSessionController>(() => ({ session: horizonSession, ensureSession: ensureHorizonSession }), [ensureHorizonSession, horizonSession]);

  let page: ReactNode;
  if (route === "/wallet/callback") page = <WalletLinkCallbackPage onConnected={connectedMobileWallet} onError={() => navigate("/")} />;
  else if (route === "/") page = <HomePage onConnectWallet={() => setWalletChoicesOpen(true)} onNavigate={navigate} />;
  else if (route === "/explore") page = <ExplorePage onNavigate={navigate} />;
  else if (route === "/models") page = <SearchPage description="Choose from the model registry returned by the live Horizon API." onNavigate={navigate} resource="models" title="AI Models" />;
  else if (route === "/agents") page = <AgentPage onConnectWallet={() => setWalletChoicesOpen(true)} sessionControl={sessionControl} wallet={wallet} />;
  else if (route === "/workflows") page = <WorkflowPage onConnectWallet={() => setWalletChoicesOpen(true)} sessionControl={sessionControl} wallet={wallet} />;
  else if (route === "/models/qwen3-1.7b/playground") page = <QwenPlaygroundPage onConnectWallet={() => setWalletChoicesOpen(true)} onNavigate={navigate} sessionControl={sessionControl} wallet={wallet} />;
  else if (route.startsWith("/models/")) page = <ModelDetailPage modelId={route.slice("/models/".length)} onNavigate={navigate} />;
  else page = <DashboardPage onConnectWallet={() => setWalletChoicesOpen(true)} sessionControl={sessionControl} wallet={wallet} />;

  return <div className={dark ? "app-shell dark" : "app-shell"}><Header connectingWallet={connectingWallet} dark={dark} onConnectWallet={() => setWalletChoicesOpen(true)} onNavigate={navigate} onToggleTheme={() => setDark((value) => !value)} route={route} wallet={wallet} />{walletError && <WalletAlert message={walletError} onDismiss={() => setWalletError("")} />}{walletChoicesOpen && <WalletConnectionDialog busy={connectingWallet} onBrowser={() => void connectWallet()} onClose={() => setWalletChoicesOpen(false)} onMobile={() => void connectMobileWallet()} />}<div className="page-frame" key={route}>{page}</div><Footer onNavigate={navigate} /></div>;
}
